import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrToStr, resolveImg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

export const ImageUploader = ({ value, onChange, testId = "image-upload" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { onChange(await uploadFile(file)); toast.success("Image uploaded"); }
    catch (err) { toast.error(apiErrToStr(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex gap-2 items-center">
      {value && <img src={resolveImg(value)} alt="" className="w-12 h-12 rounded object-cover border border-gold/20" />}
      <Input data-testid={`${testId}-url`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload" className="flex-1" />
      <input ref={ref} type="file" accept="image/*" hidden onChange={pick} data-testid={`${testId}-file`} />
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current.click()} data-testid={`${testId}-btn`} className="border-gold/40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
      </Button>
    </div>
  );
};

export const GalleryUploader = ({ images = [], onChange }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadFile(f));
      onChange([...images, ...urls]);
      toast.success(`${urls.length} photo(s) uploaded`);
    } catch (err) { toast.error(apiErrToStr(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const addUrl = () => { if (url.trim()) { onChange([...images, url.trim()]); setUrl(""); } };
  return (
    <div data-testid="gallery-uploader">
      <div className="flex flex-wrap gap-2 mb-2">
        {images.map((src, i) => (
          <div key={src} className="relative w-20 h-20">
            <img src={resolveImg(src)} alt="" className="w-full h-full rounded object-cover border border-gold/20" />
            <button type="button" data-testid={`gallery-remove-${i}`} onClick={() => onChange(images.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
          </div>
        ))}
        <input ref={ref} type="file" accept="image/*" multiple hidden onChange={pick} data-testid="gallery-file" />
        <button type="button" data-testid="gallery-upload-btn" disabled={busy} onClick={() => ref.current.click()} className="w-20 h-20 rounded border-2 border-dashed border-gold/40 flex flex-col items-center justify-center text-[11px] text-indigo-deep/60 hover:bg-gold/5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mb-1" />Upload</>}
        </button>
      </div>
      <div className="flex gap-2">
        <Input data-testid="gallery-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Or paste image URL" />
        <Button type="button" variant="outline" size="sm" onClick={addUrl} data-testid="gallery-url-add" className="border-gold/40">Add</Button>
      </div>
    </div>
  );
};
